/**
 * Settings → Integrations → ServiceM8 — /admin/settings/servicem8
 * Connection health, staff mapping (AWARE techs ↔ SM8 staff), campaign
 * scheduling rules and per-campaign availability preview. The API key is
 * backend-only and never displayed. Live writes stay OFF until the
 * controlled end-to-end test.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, CalendarClock, CheckCircle2, XCircle, Plug, ShieldAlert, Users, Eye, RefreshCw,
} from "lucide-react";
import api from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";
import { Button } from "@/components/ui/button";

const card = "rounded-2xl bg-white border border-slate-200 p-5";
const inputCls = "w-24 rounded-lg border border-slate-300 px-2 py-1.5 text-sm";

const REASON_LABELS = {
  beyond_booking_horizon: "Beyond booking horizon",
  inside_minimum_notice: "Inside minimum notice",
  tech_daily_limit_reached: "Tech daily limit reached",
  no_time_fits: "No appointment time fits (conflicts)",
};

const ALERT_TYPE_LABELS = {
  activity_create_update_failed: "Activity create/update failed",
  activity_cancel_failed: "Activity cancellation failed",
  technician_mismatch: "Technician mismatch (AWARE vs ServiceM8)",
  missing_staff_mapping: "Missing ServiceM8 staff mapping",
  inactive_staff_mapping: "Inactive ServiceM8 staff mapping",
  duplicate_staff_mapping: "Duplicate/ambiguous staff mapping",
  unresolved_conflict: "Unresolved date/time conflict",
  extra_activities: "Extra ServiceM8 Activities on one booking",
  missing_activity_after_sync: "No Activity after sync completed",
};

function NumField({ label, value, onChange, suffix }) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-slate-600 font-medium">{label}</span>
      <span className="flex items-center gap-2">
        <input type="number" className={inputCls} value={value ?? ""}
          data-testid={`sm8-sched-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          onChange={(e) => onChange(e.target.value)} />
        <span className="text-xs text-slate-500">{suffix}</span>
      </span>
    </label>
  );
}

export default function AdminServiceM8Settings() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [sched, setSched] = useState(null);
  const [sm8Staff, setSm8Staff] = useState(null);
  const [awareTechs, setAwareTechs] = useState([]);
  const [mappings, setMappings] = useState({});
  const [campaigns, setCampaigns] = useState([]);
  const [previewCid, setPreviewCid] = useState("");
  const [preview, setPreview] = useState(null);
  const [appts, setAppts] = useState(null);
  const [syncLog, setSyncLog] = useState(null);
  const [clientMatchAid, setClientMatchAid] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [writesAudit, setWritesAudit] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [alertsFilter, setAlertsFilter] = useState("open");
  const [alertsBusy, setAlertsBusy] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/integrations/servicem8").then(({ data }) => {
      setStatus(data);
      const m = {};
      (data.staff_mappings || []).forEach((r) => {
        m[r.aware_email] = { uuid: r.sm8_staff_uuid, eligible: r.campaign_eligible !== false, name: r.name };
      });
      setMappings(m);
    }).catch(() => toast.error("Failed to load ServiceM8 status"));
    api.get("/admin/integrations/servicem8/scheduling-settings")
      .then(({ data }) => setSched(data)).catch(() => {});
    api.get("/admin/integrations/servicem8/aware-technicians")
      .then(({ data }) => setAwareTechs(data.technicians || [])).catch(() => {});
    api.get("/admin/property-campaigns")
      .then(({ data }) => setCampaigns(data.campaigns || [])).catch(() => {});
    api.get("/admin/integrations/servicem8/live-writes/audit")
      .then(({ data }) => setWritesAudit(data.entries || [])).catch(() => {});
    api.get("/admin/integrations/servicem8/alerts", { params: { status: alertsFilter } })
      .then(({ data }) => setAlerts(data.alerts || [])).catch(() => {});
  }, [alertsFilter]);
  useEffect(load, [load]);

  const resolveAlert = async (id) => {
    setAlertsBusy(id);
    try {
      await api.post(`/admin/integrations/servicem8/alerts/${id}/resolve`, { note: "Resolved by admin" });
      toast.success("Alert resolved");
      load();
      window.dispatchEvent(new Event("sm8-alerts-changed"));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to resolve"); }
    finally { setAlertsBusy(null); }
  };

  const testConnection = async () => {
    setBusy(true); setTestResult(null);
    try {
      const { data } = await api.post("/admin/integrations/servicem8/test-connection");
      setTestResult({ ok: true, msg: `Connected — ${data.active_staff} active ServiceM8 staff found` });
      api.get("/admin/integrations/servicem8/staff")
        .then(({ data: d }) => setSm8Staff(d.staff || []))
        .catch(() => toast.error("Staff list failed"));
      load();
    } catch (e) {
      setTestResult({ ok: false, msg: e.response?.data?.detail || "Connection failed" });
    } finally { setBusy(false); }
  };

  const loadStaff = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/integrations/servicem8/staff");
      setSm8Staff(data.staff || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load ServiceM8 staff"); }
    finally { setBusy(false); }
  };

  const saveMappings = async () => {
    const rows = Object.entries(mappings)
      .filter(([, v]) => v.uuid)
      .map(([email, v]) => ({
        aware_email: email, sm8_staff_uuid: v.uuid,
        name: v.name || "", campaign_eligible: v.eligible !== false,
      }));
    setBusy(true);
    try {
      await api.put("/admin/integrations/servicem8/staff-mappings", { staff_mappings: rows });
      toast.success(`Saved ${rows.length} staff mapping${rows.length === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const saveSched = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/integrations/servicem8/scheduling-settings", sched);
      setSched(data); toast.success("Scheduling settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const LIVE_WRITE_CATEGORIES = [
    { key: "quote", label: "Quote & Customer Sync",
      desc: "Creates/updates the ServiceM8 Quote, Company and Contact for a booking." },
    { key: "assignment", label: "Technician / Activity Sync",
      desc: "Moves the technician's calendar Activity on reschedule, reassignment or cancellation." },
    { key: "diary", label: "Job Diary Writes",
      desc: "Posts AWARE BOOKING CHANGE and JASON CUSTOMER MESSAGE notes to the Job Diary." },
    { key: "report", label: "Report PDF Attachments",
      desc: "Attaches the final inspection/health-check PDF to the Quote." },
  ];

  const toggleLiveWrites = async (category, currentlyEnabled) => {
    const label = LIVE_WRITE_CATEGORIES.find((c) => c.key === category)?.label || category;
    const enabling = !currentlyEnabled;
    if (enabling && !window.confirm(
      `Enable ServiceM8 LIVE WRITES for "${label}"?\n\nThis allows AWARE to make REAL ${label.toLowerCase()} changes in your live ServiceM8 account.`)) return;
    setBusy(true);
    try {
      await api.post("/admin/integrations/servicem8/live-writes", { category, enabled: enabling });
      toast.success(`${label} ${enabling ? "ENABLED" : "disabled"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Toggle failed"); }
    finally { setBusy(false); }
  };

  const runPreview = async () => {
    if (!previewCid) return;
    setBusy(true); setPreview(null);
    try {
      const { data } = await api.get(`/admin/integrations/servicem8/availability-preview/${previewCid}`);
      setPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };

  const setMap = (email, patch) =>
    setMappings((m) => ({ ...m, [email]: { ...(m[email] || {}), ...patch } }));

  const loadAppts = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/integrations/servicem8/appointments");
      setAppts(data.appointments || []);
    } catch (e) { toast.error("Failed to load appointments"); }
    finally { setBusy(false); }
  };

  const syncAppt = async (a) => {
    if (!window.confirm(
      `Run the CONTROLLED ServiceM8 sync for ${a.street_address}, ${a.suburb} (${a.date} ${a.start_time})?\n\nThis creates a REAL company, job, contact, activity and note in ServiceM8. Live writes must be enabled.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/integrations/servicem8/appointments/${a.id}/sync`);
      toast.success(`Synced — SM8 job ${data.job_uuid}${data.staff_mapped ? " with scheduled activity" : " (no activity — tech not SM8-mapped)"}`);
      loadAppts();
    } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
    finally { setBusy(false); }
  };

  const viewLog = async (aid) => {
    try {
      const { data } = await api.get(`/admin/integrations/servicem8/sync-log/${aid}`);
      setSyncLog({ aid, rows: data.log });
    } catch { toast.error("Failed to load sync log"); }
  };

  const resolveClientMatch = async (aid, action, companyUuid) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/integrations/servicem8/appointments/${aid}/resolve-client-match`,
        { action, company_uuid: companyUuid });
      if (data.ok) toast.success("Client match resolved — job created in ServiceM8");
      else toast.error(data.error || "Resolved, but the sync still didn't complete — check the log");
      setClientMatchAid(null);
      loadAppts();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to resolve client match"); }
    finally { setBusy(false); }
  };

  if (!status || !sched) {
    return <PortalShell><div className="p-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div></PortalShell>;
  }

  const badge = status.status === "configured"
    ? <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5 text-xs font-semibold"><CheckCircle2 className="w-3.5 h-3.5" />Connected</span>
    : status.status === "failed"
      ? <span className="inline-flex items-center gap-1 text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2.5 py-0.5 text-xs font-semibold"><XCircle className="w-3.5 h-3.5" />Failed</span>
      : <span className="inline-flex items-center gap-1 text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-2.5 py-0.5 text-xs font-semibold">Not configured</span>;

  return (
    <PortalShell>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="sm8-settings-page">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700">Settings · Integrations</div>
        <div className="flex items-center gap-3 mt-1 mb-1">
          <CalendarClock className="w-6 h-6 text-cyan-700" />
          <h1 className="text-2xl font-bold text-slate-900">ServiceM8 Scheduling</h1>
          {badge}
        </div>
        <p className="text-sm text-slate-500 mb-6">
          AWARE remains the customer-facing scheduler. ServiceM8 is checked for technician
          conflicts before slots are offered. The API key lives only in the backend environment.
        </p>

        {/* Connection */}
        <div className={`${card} mb-5`} data-testid="sm8-connection-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="font-semibold text-slate-900 flex items-center gap-2"><Plug className="w-4 h-4" />Connection</div>
              <div className="text-xs text-slate-500 mt-1">
                {status.last_success_at ? `Last success: ${new Date(status.last_success_at).toLocaleString()}` : "No successful call yet"}
                {status.last_error ? ` · Last error: ${status.last_error}` : ""}
              </div>
            </div>
            <Button onClick={testConnection} disabled={busy} data-testid="sm8-test-connection-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Test connection"}
            </Button>
          </div>
          {testResult && (
            <div className={`mt-3 text-sm rounded-lg px-3 py-2 ${testResult.ok ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`} data-testid="sm8-test-result">
              {testResult.msg}
            </div>
          )}
        </div>

        {/* Live writes — four independent categories (Feb 2026) */}
        <div className={card + " mb-5"} data-testid="sm8-live-writes-card">
          <div className="font-semibold text-slate-900 flex items-center gap-2 mb-1">
            <ShieldAlert className="w-4 h-4 text-slate-400" />
            Live writes to ServiceM8
          </div>
          <div className="text-xs text-slate-500 mb-4">
            Each category is gated independently — enabling one never turns on
            another. Schedule reads always work regardless of these switches.
          </div>
          <div className="space-y-2">
            {LIVE_WRITE_CATEGORIES.map(({ key, label, desc }) => {
              const field = `live_writes_${key}`;
              const enabled = status[field] === true;
              return (
                <div key={key}
                  className={`flex items-center justify-between gap-3 flex-wrap rounded-xl border px-4 py-3 ${enabled ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200"}`}
                  data-testid={`sm8-live-writes-row-${key}`}>
                  <div>
                    <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                      {label}
                      <span className={`text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 ${enabled ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-600"}`}
                        data-testid={`sm8-live-writes-badge-${key}`}>
                        {enabled ? "Live" : "Off"}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 max-w-lg">{desc}</div>
                    {key === "report" && !enabled && (
                      <div className="text-xs font-semibold text-amber-700 mt-1.5 flex items-center gap-1"
                        data-testid="sm8-report-off-warning">
                        <ShieldAlert className="w-3.5 h-3.5" />
                        Intentionally OFF — enable only after a dedicated controlled test.
                      </div>
                    )}
                  </div>
                  <Button variant={enabled ? "destructive" : "outline"} size="sm"
                    onClick={() => toggleLiveWrites(key, enabled)} disabled={busy}
                    data-testid={`sm8-live-writes-toggle-${key}`}>
                    {enabled ? "Disable" : "Enable"}
                  </Button>
                </div>
              );
            })}
          </div>

          {/* Immutable audit trail — insert-only, no edit/delete path exists */}
          <div className="mt-4 pt-4 border-t border-slate-100" data-testid="sm8-live-writes-audit">
            <div className="text-xs font-semibold text-slate-600 mb-2">Recent changes</div>
            {writesAudit.length === 0 ? (
              <div className="text-xs text-slate-400">No changes recorded yet.</div>
            ) : (
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {writesAudit.map((e) => {
                  const label = LIVE_WRITE_CATEGORIES.find((c) => c.key === e.category)?.label || e.category;
                  return (
                    <div key={e.id} className="text-xs text-slate-600 flex items-center gap-2 flex-wrap"
                      data-testid={`sm8-audit-row-${e.id}`}>
                      <span className="font-medium text-slate-800">{label}</span>
                      <span className={e.previous_value ? "text-rose-600" : "text-slate-400"}>
                        {e.previous_value ? "ON" : "OFF"}
                      </span>
                      →
                      <span className={e.new_value ? "text-emerald-700 font-semibold" : "text-slate-400"}>
                        {e.new_value ? "ON" : "OFF"}
                      </span>
                      <span className="text-slate-400">·</span>
                      <span>{e.changed_by || "admin"}</span>
                      <span className="text-slate-400">·</span>
                      <span>{e.changed_at ? new Date(e.changed_at).toLocaleString() : ""}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Staff Sync Alerts — Feb 2026 */}
        <div className={`${card} mb-5`} data-testid="sm8-alerts-card">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="font-semibold text-slate-900 flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-amber-600" /> Staff Sync Alerts
            </div>
            <div className="flex items-center gap-2">
              <div className="flex rounded-full border border-slate-200 overflow-hidden text-xs font-semibold">
                {["open", "resolved"].map((f) => (
                  <button key={f} data-testid={`sm8-alerts-filter-${f}`}
                    onClick={() => setAlertsFilter(f)}
                    className={`px-3 py-1.5 ${alertsFilter === f ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>
                    {f === "open" ? "Open" : "Resolved"}
                  </button>
                ))}
              </div>
              <Button size="sm" variant="outline" data-testid="sm8-alerts-sweep-now-btn"
                onClick={async () => {
                  try { await api.post("/admin/integrations/servicem8/alerts/sweep-now"); toast.success("Sweep complete"); load(); window.dispatchEvent(new Event("sm8-alerts-changed")); }
                  catch { toast.error("Sweep failed"); }
                }}>
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Check now
              </Button>
            </div>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            Scheduling mismatches between AWARE and ServiceM8 that need a human — failed Activity
            writes, staff mapping gaps, unresolved conflicts and extra/missing Activities. Checked
            automatically every 5 minutes; duplicates of the same unresolved issue are merged, not
            repeated.
          </p>
          {alerts.length === 0 ? (
            <div className="text-sm text-slate-400" data-testid="sm8-alerts-empty">
              {alertsFilter === "open" ? "No open alerts — everything is in sync." : "No resolved alerts yet."}
            </div>
          ) : (
            <div className="space-y-2 max-h-[32rem] overflow-y-auto">
              {alerts.map((a) => (
                <div key={a.id} className={`rounded-xl border px-4 py-3 ${a.status === "open" ? "border-amber-300 bg-amber-50/50" : "border-slate-200 bg-slate-50/50"}`}
                  data-testid={`sm8-alert-row-${a.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-sm font-bold text-slate-900">
                        {ALERT_TYPE_LABELS[a.problem_type] || a.problem_type}
                        {a.occurrence_count > 1 && (
                          <span className="ml-2 text-[10px] font-bold uppercase bg-slate-200 text-slate-600 rounded-full px-2 py-0.5">
                            ×{a.occurrence_count}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-600 mt-1">{a.detail}</div>
                      {(a.context?.customer_name || a.context?.property_address || a.context?.aware_technician || a.context?.sm8_technician) && (
                        <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3">
                          {a.context?.customer_name && <span>Customer: <b>{a.context.customer_name}</b></span>}
                          {a.context?.property_address && <span>Address: {a.context.property_address}</span>}
                          {a.context?.aware_technician && <span>AWARE tech: {a.context.aware_technician}</span>}
                          {a.context?.sm8_technician && <span>ServiceM8 tech: {a.context.sm8_technician}</span>}
                        </div>
                      )}
                      <div className="text-[11px] text-slate-400 mt-1">
                        Detected {new Date(a.detected_at).toLocaleString()}
                        {a.last_sync_attempt_at && <> · Last sync attempt {new Date(a.last_sync_attempt_at).toLocaleString()} ({a.last_sync_result || "?"})</>}
                      </div>
                      {a.recommended_action && (
                        <div className="text-xs text-cyan-700 mt-1.5 font-medium">→ {a.recommended_action}</div>
                      )}
                      {a.status === "resolved" && (
                        <div className="text-[11px] text-emerald-700 mt-1.5">
                          Resolved {new Date(a.resolved_at).toLocaleString()} by {a.resolved_by} — {a.resolution_note}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {a.appointment_id && a.campaign_id && (
                        <Button size="sm" variant="outline"
                          data-testid={`sm8-alert-view-booking-${a.id}`}
                          onClick={() => navigate(`/admin/property-campaigns/${a.campaign_id}?appt=${a.appointment_id}`)}>
                          <Eye className="w-3.5 h-3.5 mr-1" /> View booking
                        </Button>
                      )}
                      {a.status === "open" && (
                        <Button size="sm" data-testid={`sm8-alert-resolve-${a.id}`}
                          disabled={alertsBusy === a.id} onClick={() => resolveAlert(a.id)}>
                          {alertsBusy === a.id ? "…" : "Mark resolved"}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Scheduling rules */}
        <div className={`${card} mb-5`} data-testid="sm8-scheduling-card">
          <div className="font-semibold text-slate-900 mb-3">Campaign scheduling rules</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <NumField label="Appointment duration" suffix="min"
              value={sched.appointment_duration_minutes}
              onChange={(v) => setSched({ ...sched, appointment_duration_minutes: v })} />
            <NumField label="Buffer" suffix="min each side"
              value={sched.buffer_minutes}
              onChange={(v) => setSched({ ...sched, buffer_minutes: v })} />
            <NumField label="Booking horizon" suffix="days"
              value={sched.booking_horizon_days}
              onChange={(v) => setSched({ ...sched, booking_horizon_days: v })} />
            <NumField label="Minimum notice" suffix="hours"
              value={sched.min_notice_hours}
              onChange={(v) => setSched({ ...sched, min_notice_hours: v })} />
            <NumField label="Max per tech" suffix="jobs/day"
              value={sched.max_jobs_per_tech_per_day}
              onChange={(v) => setSched({ ...sched, max_jobs_per_tech_per_day: v })} />
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-slate-600 font-medium">Availability provider</span>
              <select className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                value={sched.availability_provider} data-testid="sm8-provider-select"
                onChange={(e) => setSched({ ...sched, availability_provider: e.target.value })}>
                <option value="servicem8">ServiceM8 conflict check</option>
                <option value="aware_only">AWARE slots only</option>
              </select>
            </label>
          </div>
          <div className="mt-4">
            <Button onClick={saveSched} disabled={busy} data-testid="sm8-save-sched-btn">Save scheduling rules</Button>
          </div>
        </div>

        {/* Staff mapping */}
        <div className={`${card} mb-5`} data-testid="sm8-staff-mapping-card">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div className="font-semibold text-slate-900 flex items-center gap-2"><Users className="w-4 h-4" />Staff mapping — AWARE ↔ ServiceM8</div>
            <div className="flex items-center gap-2">
              <a href="/field/technicians" data-testid="sm8-manage-techs-link"
                className="text-sm font-semibold text-cyan-700 hover:underline">Manage technician accounts</a>
              <Button variant="outline" onClick={loadStaff} disabled={busy} data-testid="sm8-load-staff-btn">
                {sm8Staff ? "Reload ServiceM8 staff" : "Load ServiceM8 staff"}
              </Button>
            </div>
          </div>
          {!sm8Staff ? (
            <div className="text-sm text-slate-500">Load the ServiceM8 staff list to map technicians.</div>
          ) : awareTechs.length === 0 ? (
            <div className="text-sm text-slate-500">No approved AWARE technicians found.</div>
          ) : (
            <>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-3">AWARE technician</th>
                    <th className="py-2 pr-3">ServiceM8 staff</th>
                    <th className="py-2">Campaign eligible</th>
                  </tr>
                </thead>
                <tbody>
                  {awareTechs.map((t) => {
                    const email = (t.email || "").toLowerCase();
                    const m = mappings[email] || {};
                    return (
                      <tr key={t.id} className="border-b border-slate-100" data-testid={`sm8-map-row-${t.id}`}>
                        <td className="py-2 pr-3">
                          <div className="font-medium text-slate-800">{t.name || email}</div>
                          <div className="text-xs text-slate-500">{email}</div>
                        </td>
                        <td className="py-2 pr-3">
                          <select className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm w-full max-w-xs"
                            value={m.uuid || ""} data-testid={`sm8-map-select-${t.id}`}
                            onChange={(e) => {
                              const st = sm8Staff.find((s) => s.uuid === e.target.value);
                              setMap(email, { uuid: e.target.value, name: st ? `${st.first_name} ${st.last_name}`.trim() : "" });
                            }}>
                            <option value="">— Not mapped —</option>
                            {sm8Staff.map((s) => (
                              <option key={s.uuid} value={s.uuid}>
                                {`${s.first_name} ${s.last_name}`.trim()}{s.email ? ` (${s.email})` : ""}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="py-2">
                          <input type="checkbox" checked={m.eligible !== false}
                            data-testid={`sm8-map-eligible-${t.id}`}
                            onChange={(e) => setMap(email, { eligible: e.target.checked })} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="mt-4">
                <Button onClick={saveMappings} disabled={busy} data-testid="sm8-save-mappings-btn">Save mappings</Button>
              </div>
            </>
          )}
        </div>

        {/* Availability preview */}
        <div className={card} data-testid="sm8-preview-card">
          <div className="font-semibold text-slate-900 flex items-center gap-2 mb-3"><Eye className="w-4 h-4" />Availability preview</div>
          <div className="flex items-center gap-3 flex-wrap">
            <select className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm max-w-sm"
              value={previewCid} data-testid="sm8-preview-campaign-select"
              onChange={(e) => setPreviewCid(e.target.value)}>
              <option value="">Select a campaign…</option>
              {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name || c.id}</option>)}
            </select>
            <Button variant="outline" onClick={runPreview} disabled={busy || !previewCid} data-testid="sm8-run-preview-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Run preview"}
            </Button>
          </div>
          {preview && (
            <div className="mt-4" data-testid="sm8-preview-results">
              <div className="text-xs text-slate-500 mb-2">
                Provider: {preview.provider} · ServiceM8 checked: {String(preview.sm8_checked)}
                {preview.sm8_error ? ` · SM8 error (failed open): ${preview.sm8_error}` : ""}
              </div>
              {preview.slots.length === 0 ? (
                <div className="text-sm text-slate-500">No active future slots on this campaign.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                      <th className="py-1.5 pr-3">Date</th>
                      <th className="py-1.5 pr-3">Window</th>
                      <th className="py-1.5 pr-3">Technician</th>
                      <th className="py-1.5 pr-3">Booked</th>
                      <th className="py-1.5">Offered appointment times</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.slots.map((s) => (
                      <tr key={s.id} className="border-b border-slate-100">
                        <td className="py-1.5 pr-3">{s.date}</td>
                        <td className="py-1.5 pr-3 whitespace-nowrap">{s.start_time}–{s.end_time}</td>
                        <td className="py-1.5 pr-3">
                          {s.technician_name || <span className="text-slate-400">Unassigned</span>}
                          {s.technician_id && !s.sm8_mapped && <span className="ml-1 text-[10px] text-amber-600">(not SM8-mapped)</span>}
                        </td>
                        <td className="py-1.5 pr-3">{s.booked_count}/{s.capacity}</td>
                        <td className="py-1.5">
                          {s.included && s.times?.length
                            ? <span className="flex flex-wrap gap-1">
                                {s.times.map((t) => (
                                  <span key={t.start_time} className="inline-block rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 px-2 py-0.5 text-[11px] font-semibold">
                                    {t.start_time}–{t.end_time}
                                  </span>
                                ))}
                              </span>
                            : <span className="text-rose-700">{REASON_LABELS[s.excluded_reason] || s.excluded_reason}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        {/* Sync monitor + controlled test */}
        <div className={`${card} mt-5`} data-testid="sm8-sync-monitor-card">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div>
              <div className="font-semibold text-slate-900 flex items-center gap-2"><RefreshCw className="w-4 h-4" />Sync monitor &amp; controlled test</div>
              <div className="text-xs text-slate-500 mt-1">
                Scheduled campaign appointments and their ServiceM8 sync state. The Sync button is the controlled end-to-end test — it needs live writes enabled.
              </div>
            </div>
            <Button variant="outline" onClick={loadAppts} disabled={busy} data-testid="sm8-load-appts-btn">
              {appts ? "Refresh" : "Load appointments"}
            </Button>
          </div>
          {appts && (appts.length === 0 ? (
            <div className="text-sm text-slate-500">No scheduled campaign appointments.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                  <th className="py-1.5 pr-3">When</th>
                  <th className="py-1.5 pr-3">Property</th>
                  <th className="py-1.5 pr-3">Technician</th>
                  <th className="py-1.5 pr-3">SM8 status</th>
                  <th className="py-1.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {appts.map((a) => (
                  <tr key={a.id} className="border-b border-slate-100" data-testid={`sm8-appt-row-${a.id}`}>
                    <td className="py-2 pr-3 whitespace-nowrap">{a.date} {a.start_time}</td>
                    <td className="py-2 pr-3">{a.street_address}, {a.suburb}</td>
                    <td className="py-2 pr-3">{a.technician_name || <span className="text-slate-400">Unassigned</span>}</td>
                    <td className="py-2 pr-3">
                      {a.sm8_sync_status === "review_required"
                        ? <span className="text-amber-700 font-medium" data-testid={`sm8-review-badge-${a.id}`}
                                title={a.sm8_client_match_reason || ""}>Needs client-match review</span>
                        : a.sm8_sync_status === "synced" && a.sm8_job_uuid && !a.sm8_activity_uuid
                        ? <span className="text-amber-600 font-medium" title={a.sm8_activity_skipped_reason || ""}>Synced — NO scheduled time</span>
                        : a.sm8_sync_status === "synced"
                          ? <span className="text-emerald-700 font-medium">Synced</span>
                          : a.sm8_sync_status === "failed"
                            ? <span className="text-rose-700" title={a.sm8_sync_error || ""}>Failed</span>
                            : <span className="text-slate-500">{a.sm8_sync_status || "not synced"}</span>}
                      {a.sm8_sync_status === "review_required" && (
                        <div className="text-[10px] text-amber-600 mt-0.5 max-w-[220px]">
                          {(a.sm8_client_match_reason || "").replaceAll("_", " ")} — slot held, resolve below
                        </div>
                      )}
                      {a.sm8_sync_status === "synced" && !a.sm8_activity_uuid && a.sm8_activity_skipped_reason && (
                        <div className="text-[10px] text-amber-600 mt-0.5 max-w-[220px]">{a.sm8_activity_skipped_reason} — fix, then Re-sync</div>
                      )}
                    </td>
                    <td className="py-2 whitespace-nowrap">
                      {a.sm8_sync_status === "review_required" ? (
                        <button className="text-amber-700 hover:underline text-xs font-semibold mr-3"
                          data-testid={`sm8-review-btn-${a.id}`} disabled={busy}
                          onClick={() => setClientMatchAid(a.id)}>
                          Review client match
                        </button>
                      ) : (
                        <button className="text-cyan-700 hover:underline text-xs font-semibold mr-3"
                          data-testid={`sm8-sync-btn-${a.id}`} disabled={busy}
                          onClick={() => syncAppt(a)}>
                          {a.sm8_sync_status === "synced" ? "Re-sync" : "Sync to ServiceM8"}
                        </button>
                      )}
                      <button className="text-slate-500 hover:underline text-xs"
                        data-testid={`sm8-log-btn-${a.id}`} onClick={() => viewLog(a.id)}>Log</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ))}
          {syncLog && (
            <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-3" data-testid="sm8-sync-log">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold text-slate-700">Sync log</div>
                <button className="text-xs text-slate-500 hover:underline" onClick={() => setSyncLog(null)}>Close</button>
              </div>
              {syncLog.rows.length === 0 ? (
                <div className="text-xs text-slate-500">No sync activity recorded for this appointment.</div>
              ) : syncLog.rows.map((r) => (
                <div key={r.id} className="text-xs py-1 border-b border-slate-100 last:border-0">
                  <span className={r.ok ? "text-emerald-700" : "text-rose-700"}>{r.ok ? "OK" : "FAIL"}</span>
                  {" · "}{r.action}{r.detail ? ` — ${r.detail}` : ""}
                  <span className="text-slate-400"> · {new Date(r.at).toLocaleString()} · {r.by}</span>
                </div>
              ))}
            </div>
          )}
          {clientMatchAid && (() => {
            const a = (appts || []).find((x) => x.id === clientMatchAid);
            if (!a) return null;
            const candidates = a.sm8_client_match_candidates || [];
            return (
              <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-4" data-testid="sm8-client-match-panel">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-semibold text-amber-900">
                    Resolve client match — {a.street_address}, {a.suburb}
                  </div>
                  <button className="text-xs text-slate-500 hover:underline" onClick={() => setClientMatchAid(null)}>Close</button>
                </div>
                <div className="text-xs text-amber-800 mb-3">
                  Reason: {(a.sm8_client_match_reason || "").replaceAll("_", " ")}. No ServiceM8 client or job has
                  been created yet — the booking slot is held.
                </div>
                {candidates.length === 0 ? (
                  <div className="text-xs text-slate-500 mb-3">No candidate client details were captured.</div>
                ) : (
                  <div className="space-y-2 mb-3">
                    {candidates.map((c) => (
                      <div key={c.company_uuid} className="rounded-lg bg-white border border-slate-200 p-3 flex items-center justify-between gap-3"
                        data-testid={`sm8-match-candidate-${c.company_uuid}`}>
                        <div className="text-xs">
                          <div className="font-semibold text-slate-800">{c.name || "(no name on file)"}</div>
                          <div className="text-slate-500">{c.billing_address || "no address on file"}</div>
                          <div className="text-slate-500">{c.mobile || "—"} · {c.email || "—"}</div>
                          <div className="text-slate-400">matched by: {(c.matched_by || []).join(", ") || "—"}</div>
                        </div>
                        <Button size="sm" variant="outline" className="rounded-full shrink-0" disabled={busy}
                          data-testid={`sm8-use-existing-btn-${c.company_uuid}`}
                          onClick={() => resolveClientMatch(a.id, "use_existing", c.company_uuid)}>
                          Use this client
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <Button size="sm" variant="outline" className="rounded-full" disabled={busy}
                  data-testid="sm8-confirm-new-client-btn"
                  onClick={() => resolveClientMatch(a.id, "create_new")}>
                  This is a new client — create one
                </Button>
              </div>
            );
          })()}
        </div>
      </div>
    </PortalShell>
  );
}
